import { EMPLOYEE_CERTIFICATIONS, SAFETY_INITIATIVES } from "./employeeCertifications";
import {
  COMPANY_CERTIFICATIONS,
  CompanyCertificationRecord,
  GOVERNMENT_COMPANY_CERT_IDS,
  PRIVATE_COMPANY_CERT_IDS,
  PUBLIC_COMPANY_CERT_IDS,
} from "./companyCertifications";
import { CertificationRecord } from "./certificateTypes";
import { EMPLOYEE_FEATURE_CERT_IDS } from "./onmywayFeatures";
export interface CertificationParentProgram {
  id: string;
  name: string;
  companyCertificateId?: string;
  subCertificateIds: string[];
}

export type CompanyBundleType =
  | "public"
  | "government_contractor"
  | "private";

export type CompliancePriority = "high" | "medium" | "low";

export interface CertificateMetadata {
  companyTypes: CompanyBundleType[];
  farsDfars2026Required: boolean;
  oshaPriority: CompliancePriority;
  dolPriority: CompliancePriority;
}

const allTypes: CompanyBundleType[] = [
  "public",
  "private",
  "government_contractor",
];

const featureMeta = (
  overrides: Partial<CertificateMetadata> = {}
): CertificateMetadata => ({
  companyTypes: allTypes,
  farsDfars2026Required: true,
  oshaPriority: "high",
  dolPriority: "medium",
  ...overrides,
});

export const CERTIFICATE_METADATA: Record<string, CertificateMetadata> = {
  "archie-securepath-personal-protection": featureMeta(),
  "ridewatch-transportation-safety": featureMeta(),
  "verifyai-digital-identity-protection": featureMeta({
    companyTypes: ["public", "government_contractor"],
  }),
  "defensive-driving-distracted": featureMeta(),
  "mental-health-workplace-wellness": featureMeta(),
  "personal-safety-travel-concierge": featureMeta({
    companyTypes: ["public", "government_contractor"],
    farsDfars2026Required: false,
  }),
  "safety-in-motion-protection": featureMeta(),
  "lone-worker-remote-protection": featureMeta(),
  "workplace-emergency-panic-response": featureMeta(),
  "safety-engagement-participation": featureMeta({
    oshaPriority: "medium",
    dolPriority: "low",
  }),
  "safety-policy-acknowledgement": featureMeta(),
};

export interface CertificateBundle {
  id: CompanyBundleType;
  label: string;
  shortLabel: string;
  certificationProgramName: string;
  description: string;
  parentPrograms: CertificationParentProgram[];
  certificateIds: string[];
  companyCertificateIds: string[];
}

export const FARS_DFARS_2026_INFO = {
  effectiveDate: "FY2026",
  title: "FAR/DFARS 2026 Compliance Certification",
  summary:
    "Federal acquisition requirements under the Federal Acquisition Regulation (FAR) and Defense Federal Acquisition Regulation Supplement (DFARS) emphasize, effective FY2026, expanded documentation of employee safety performance, training records, and workforce protection programs. OnMyWay helps employers align internal safety documentation and training completion records with contractor compliance readiness.",
  disclaimer:
    "OnMyWay provides internal training completion and readiness certifications only. These are not FAR, DFARS, OSHA, or Department of Labor government-issued credentials.",
};

/** Required FARS/DFARS certs = official OnMyWay protection features only (onmyway.com) */
export const FARS_DFARS_2026_REQUIRED_CERT_IDS: string[] = [
  ...EMPLOYEE_FEATURE_CERT_IDS,
];

const ALL_FEATURE_CERTS = [...EMPLOYEE_FEATURE_CERT_IDS];
const ALL_FEATURE_PLUS_POLICY = [
  ...ALL_FEATURE_CERTS,
  "safety-policy-acknowledgement",
];

/** Public companies — SEC-aligned workforce training (subset, not full catalog). */
const PUBLIC_EMPLOYEE_CERT_IDS = [
  "verifyai-digital-identity-protection",
  "safety-engagement-participation",
  "archie-securepath-personal-protection",
  "workplace-emergency-panic-response",
  "mental-health-workplace-wellness",
  "lone-worker-remote-protection",
  "safety-in-motion-protection",
  "defensive-driving-distracted",
  "safety-policy-acknowledgement",
];

/** Private companies — all 10 protection programs + policy acknowledgement. */
const PRIVATE_EMPLOYEE_CERT_IDS = [
  ...EMPLOYEE_FEATURE_CERT_IDS,
  "safety-policy-acknowledgement",
];

export const CERTIFICATE_BUNDLES: CertificateBundle[] = [
  {
    id: "public",
    label: "Public Companies",
    shortLabel: "Public",
    certificationProgramName: "SEC Investor Mitigation Reporting",
    description:
      "Public company program — SEC investor mitigation reporting and employee safety compliance training.",
    parentPrograms: [
      {
        id: "sec-investor-mitigation",
        name: "SEC Investor Mitigation Reporting",
        companyCertificateId: "sec-investor-mitigation-reporting",
        subCertificateIds: PUBLIC_EMPLOYEE_CERT_IDS,
      },
    ],
    certificateIds: [...PUBLIC_EMPLOYEE_CERT_IDS],
    companyCertificateIds: [...PUBLIC_COMPANY_CERT_IDS],
  },
  {
    id: "government_contractor",
    label: "Government Contractors",
    shortLabel: "Gov Contractor",
    certificationProgramName: "FAR/DFARS 2026 Compliance Certification",
    description:
      "Federal contractor program — FAR/DFARS 2026 compliance and federal employee safety parent certifications, with OnMyWay feature training underneath.",
    parentPrograms: [
      {
        id: "far-dfars-2026-compliance",
        name: "FAR/DFARS 2026 Compliance Certification",
        companyCertificateId: "fca-protection-program-govcon",
        subCertificateIds: ALL_FEATURE_PLUS_POLICY,
      },
    ],
    certificateIds: ALL_FEATURE_PLUS_POLICY,
    companyCertificateIds: [...GOVERNMENT_COMPANY_CERT_IDS],
  },
  {
    id: "private",
    label: "Private Companies",
    shortLabel: "Private",
    certificationProgramName: "Private Company Employee Safety/Insurance Certification",
    description:
      "Private company employee safety and insurance program — core OnMyWay protection training with insurance-aligned documentation.",
    parentPrograms: [
      {
        id: "private-employee-safety-insurance",
        name: "Private Company Employee Safety/Insurance Certification",
        subCertificateIds: PRIVATE_EMPLOYEE_CERT_IDS,
      },
    ],
    certificateIds: [...PRIVATE_EMPLOYEE_CERT_IDS],
    companyCertificateIds: [...PRIVATE_COMPANY_CERT_IDS],
  },
];

export type SafetyInitiative = CertificationRecord;

export const getBundleByType = (
  type: CompanyBundleType
): CertificateBundle | undefined =>
  CERTIFICATE_BUNDLES.find((bundle) => bundle.id === type);

export const getInitiativesForIds = (
  certificateIds: string[]
): CertificationRecord[] =>
  certificateIds
    .map((id) => EMPLOYEE_CERTIFICATIONS.find((cert) => cert.id === id))
    .filter((cert): cert is CertificationRecord => Boolean(cert));

export const getCompanyCertsForIds = (
  certificateIds: string[]
): CertificationRecord[] =>
  certificateIds
    .map((id) => COMPANY_CERTIFICATIONS.find((cert) => cert.id === id))
    .filter((cert): cert is CompanyCertificationRecord => Boolean(cert));

export const getInitiativesForBundle = (
  type: CompanyBundleType
): CertificationRecord[] => {
  const bundle = getBundleByType(type);
  if (!bundle) return [];
  return getInitiativesForIds(bundle.certificateIds);
};

export const getCompanyCertsForBundle = (
  type: CompanyBundleType
): CertificationRecord[] => {
  const bundle = getBundleByType(type);
  if (!bundle) return [];
  return getCompanyCertsForIds(bundle.companyCertificateIds);
};

export const getFarsDfars2026Initiatives = (): CertificationRecord[] =>
  getInitiativesForIds([...EMPLOYEE_FEATURE_CERT_IDS]);

export const isCertInBundle = (
  certId: string,
  bundleType: CompanyBundleType
): boolean => {
  const bundle = getBundleByType(bundleType);
  return bundle?.certificateIds.includes(certId) ?? false;
};

export const getCertificateMetadata = (
  certId: string
): CertificateMetadata | undefined => CERTIFICATE_METADATA[certId];

export { SAFETY_INITIATIVES, EMPLOYEE_CERTIFICATIONS, COMPANY_CERTIFICATIONS };
